import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { httpClient } from '../../providers/dataProvider';
import useConfig from '../../hooks/useConfig';
import { FileUploadState } from './types';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 Mo
const ACCEPTED_MIME_TYPE = 'application/pdf';

const useStyles = makeStyles({
  container: {
    marginTop: '20px',
    marginBottom: '16px',
  },
  dropzone: {
    border: '2px dashed #ccc',
    borderRadius: '4px',
    padding: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    color: '#666',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  dropzoneActive: {
    borderColor: '#1976d2',
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
  },
  icon: {
    marginBottom: '8px',
  },
  fileList: {
    marginTop: '8px',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    fontSize: '14px',
  },
  fileName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  success: {
    color: '#4caf50',
  },
  error: {
    color: '#f44336',
  },
});

type AttachmentResponse = {
  id: string | number;
  upload_url: string;
};

const PurchaseOrdersDropzone: FunctionComponent = () => {
  const classes = useStyles();
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const config = useConfig();

  const [files, setFiles] = useState<FileUploadState[]>([]);

  const updateFileState = (name: string, patch: Partial<FileUploadState>) => {
    setFiles((prev) =>
      prev.map((file) => (file.name === name ? { ...file, ...patch } : file))
    );
  };

  const uploadFile = async (file: File): Promise<boolean> => {
    try {
      const { json } = await httpClient(`${config.apiUrl}/attachments`, {
        method: 'POST',
        body: JSON.stringify({
          name: file.name,
          mime_type: file.type,
          size: file.size,
          kind: 'purchase_order',
          file_uuid: uuidv4(),
        }),
      });
      const { id, upload_url } = json as AttachmentResponse;

      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type },
      });

      await httpClient(`${config.apiUrl}/purchase_orders`, {
        method: 'POST',
        body: JSON.stringify({ attachment_id: id }),
      });

      updateFileState(file.name, { status: 'success' });

      return true;
    } catch (error: any) {
      updateFileState(file.name, {
        status: 'error',
        reason: t('resource.purchase_orders.dropzone.error.upload_failed'),
      });

      return false;
    }
  };

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: File[]) => {
    const rejected: FileUploadState[] = rejectedFiles.map((file) => ({
      name: file.name,
      status: 'error',
      reason:
        file.type !== ACCEPTED_MIME_TYPE
          ? t('resource.purchase_orders.dropzone.error.invalid_format')
          : t('resource.purchase_orders.dropzone.error.file_too_large'),
    }));

    const uploading: FileUploadState[] = acceptedFiles.map((file) => ({
      name: file.name,
      status: 'uploading',
    }));

    setFiles((prev) => [...prev, ...rejected, ...uploading]);

    if (acceptedFiles.length === 0) {
      if (rejected.length > 0) {
        notify(
          t('resource.purchase_orders.dropzone.notify.all_rejected', {
            count: rejected.length,
          }),
          'warning'
        );
      }

      return;
    }

    Promise.allSettled(acceptedFiles.map(uploadFile)).then((results) => {
      const successCount = results.filter(
        (result) => result.status === 'fulfilled' && result.value
      ).length;
      const failureCount = acceptedFiles.length - successCount;

      refresh();
      notify(
        t('resource.purchase_orders.dropzone.notify.summary', {
          sent: successCount,
          rejected: failureCount + rejected.length,
        }),
        failureCount + rejected.length > 0 ? 'warning' : 'success'
      );
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_MIME_TYPE,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    onDrop,
  });

  return (
    <div className={classes.container}>
      <div
        {...getRootProps()}
        className={`${classes.dropzone} ${
          isDragActive ? classes.dropzoneActive : ''
        }`}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon className={classes.icon} fontSize="large" />
        <div>{t('resource.purchase_orders.dropzone.label')}</div>
      </div>

      {files.length > 0 && (
        <div className={classes.fileList}>
          {files.map((file, index) => (
            <div className={classes.fileRow} key={`${file.name}-${index}`}>
              {file.status === 'uploading' && <CircularProgress size={16} />}
              {file.status === 'success' && (
                <CheckCircleIcon className={classes.success} fontSize="small" />
              )}
              {file.status === 'error' && (
                <ErrorIcon className={classes.error} fontSize="small" />
              )}
              <span className={classes.fileName}>{file.name}</span>
              {file.reason && (
                <span className={classes.error}>{file.reason}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersDropzone;
